/**
 * [A3] SubscriptionActivationWatcher
 *
 * Shown immediately after Razorpay payment succeeds. Polls GET /billing/subscription
 * every 2 s (max 30 s) waiting for the webhook to activate the subscription.
 *
 * States:
 *   activating — non-dismissable spinner; backdrop blocks user interaction
 *   success    — green check, 1.5 s auto-transition into UpgradeSuccessModal via onSuccess
 *   delayed    — webhook not arrived in 30 s; "Continue to dashboard" button
 *   failed     — poll threw; "Contact support" + "Try again"
 *
 * Figma: [A3] SubscriptionActivationWatcher
 */
import React, { useEffect, useRef, useState } from "react";
import { BillingAPI, SubscriptionStatus } from "../../services/billing.service";

type WatcherState = "activating" | "success" | "delayed" | "failed";

interface Props {
    planLabel: string;
    onSuccess: (status: SubscriptionStatus) => void;
    onDismiss: () => void;
    onRetry: () => void;
}

const POLL_INTERVAL_MS = 2_000;
const MAX_POLLS = 15; // 30 s total

export const SubscriptionActivationWatcher: React.FC<Props> = ({
    planLabel,
    onSuccess,
    onDismiss,
    onRetry,
}) => {
    const [state, setState] = useState<WatcherState>("activating");
    const pollCount = useRef(0);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const clearTimer = () => {
        if (timerRef.current !== null) clearTimeout(timerRef.current);
    };

    useEffect(() => {
        const checkOnce = async (): Promise<boolean> => {
            try {
                const status = await BillingAPI.getSubscription();
                const isActive =
                    status.has_subscription &&
                    (status.status === "activated" || status.status === "active" || status.status === "authenticated");
                if (isActive) {
                    setState("success");
                    timerRef.current = setTimeout(() => onSuccess(status), 1_500);
                    return true;
                }
            } catch {
                setState("failed");
                return true;
            }
            return false;
        };

        const poll = async () => {
            const done = await checkOnce();
            if (done) return;

            pollCount.current += 1;
            if (pollCount.current >= MAX_POLLS) {
                setState("delayed");
                return;
            }

            timerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
        };

        // Re-check on tab focus while in delayed state — webhook may have
        // arrived while the user was looking at another tab.
        const onFocus = () => { if (pollCount.current >= MAX_POLLS) checkOnce(); };
        window.addEventListener("focus", onFocus);

        timerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
        return () => { clearTimer(); window.removeEventListener("focus", onFocus); };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            {/* Inner click stops bubble so backdrop never triggers a dismiss */}
            <div
                className="bg-white rounded-2xl shadow-2xl max-w-[560px] w-full px-8 py-10 text-center"
                onClick={(e) => e.stopPropagation()}
            >
                {state === "activating" && <ActivatingView planLabel={planLabel} />}
                {state === "success" && <SuccessView planLabel={planLabel} />}
                {state === "delayed" && <DelayedView onContinue={() => { clearTimer(); onDismiss(); }} />}
                {state === "failed" && <FailedView onRetry={() => { clearTimer(); onRetry(); }} />}
            </div>
        </div>
    );
};

// ── Sub-views ──────────────────────────────────────────────────────────────────

const ActivatingView: React.FC<{ planLabel: string }> = ({ planLabel }) => (
    <>
        <div className="mx-auto mb-5 w-14 h-14 rounded-full bg-blue-50 flex items-center justify-center">
            <svg
                className="animate-spin"
                width="28" height="28" viewBox="0 0 24 24" fill="none"
                stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            >
                <path d="M21 12a9 9 0 1 1-6.219-8.56" />
            </svg>
        </div>
        <h2 className="text-lg font-bold text-slate-900 mb-2">
            Activating your {planLabel} plan…
        </h2>
        <p className="text-sm text-slate-500">
            This usually takes a few seconds. Don't close this window.
        </p>
    </>
);

const SuccessView: React.FC<{ planLabel: string }> = ({ planLabel }) => (
    <>
        <div className="mx-auto mb-5 w-14 h-14 rounded-full bg-green-100 flex items-center justify-center">
            <svg
                width="28" height="28" viewBox="0 0 24 24" fill="none"
                stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
            >
                <polyline points="20 6 9 17 4 12" />
            </svg>
        </div>
        <h2 className="text-lg font-bold text-slate-900 mb-2">
            {planLabel} activated
        </h2>
        <p className="text-sm text-slate-500">Taking you to your new features…</p>
    </>
);

const DelayedView: React.FC<{ onContinue: () => void }> = ({ onContinue }) => (
    <>
        <div className="mx-auto mb-5 w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center">
            <svg
                width="28" height="28" viewBox="0 0 24 24" fill="none"
                stroke="#d97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            >
                <path d="M5 22h14M5 2h14M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l-4.414 4.414A2 2 0 0 0 7 17.828V22M17 2v4.172a2 2 0 0 1-.586 1.414L12 12l-4.414-4.414A2 2 0 0 1 7 6.172V2" />
            </svg>
        </div>
        <h2 className="text-lg font-bold text-slate-900 mb-2">Still processing</h2>
        <p className="text-sm text-slate-500 mb-6">
            We'll email you when your features are active.
        </p>
        <button
            onClick={onContinue}
            className="px-8 py-2.5 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
        >
            Continue to dashboard
        </button>
    </>
);

const FailedView: React.FC<{ onRetry: () => void }> = ({ onRetry }) => (
    <>
        <div className="mx-auto mb-5 w-14 h-14 rounded-full bg-red-100 flex items-center justify-center">
            <svg
                width="28" height="28" viewBox="0 0 24 24" fill="none"
                stroke="#dc2626" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
            >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
        </div>
        <h2 className="text-lg font-bold text-slate-900 mb-2">
            We couldn't activate your plan
        </h2>
        <p className="text-sm text-slate-500 mb-6">
            Please contact support if this keeps happening.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
                href="mailto:support@swasthiq.in"
                className="w-full sm:w-auto px-6 py-2.5 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors text-center"
            >
                Contact support
            </a>
            <button
                onClick={onRetry}
                className="w-full sm:w-auto px-6 py-2.5 text-sm font-semibold text-slate-700 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
            >
                Try again
            </button>
        </div>
    </>
);
